'use client';

import React, { createContext, useState, useEffect, useCallback } from 'react';
import en from './translations/en.json';
import es from './translations/es.json';
import fr from './translations/fr.json';
import de from './translations/de.json';
import zh from './translations/zh.json';
import ja from './translations/ja.json';
import pt from './translations/pt.json';
import ru from './translations/ru.json';
import ar from './translations/ar.json';

const translations = { en, es, fr, de, zh, ja, pt, ru, ar };

export const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  zh: '中文',
  ja: '日本語',
  pt: 'Português',
  ru: 'Русский',
  ar: 'العربية',
};

const getLanguageFromBrowser = () => {
  if (typeof navigator === 'undefined') return 'en';
  const browserLang = navigator.language?.split('-')[0] || 'en';
  return SUPPORTED_LANGUAGES[browserLang] ? browserLang : 'en';
};

export function LanguageProvider({ children }) {
  // Default to 'en' on the server; client picks up the saved/browser pref
  // in the effect below to avoid a hydration mismatch.
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('app_language') : null;
    if (stored && SUPPORTED_LANGUAGES[stored]) {
      setLanguage(stored);
    } else {
      setLanguage(getLanguageFromBrowser());
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('app_language', language);
    }
  }, [language]);

  const t = useCallback((section, key) => {
    return translations[language]?.[section]?.[key] || 
           translations['en']?.[section]?.[key] || 
           key;
  }, [language]);

  const changeLanguage = useCallback((newLang) => {
    if (SUPPORTED_LANGUAGES[newLang]) {
      setLanguage(newLang);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage, SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = React.useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}